/**
 * Flattens the API's related-data envelope into a template scope.
 *
 * The endpoint reports each related block separately so one failing table does
 * not fail the request:
 *
 *   { splits: { status: "ok",    data: [ ... ] },
 *     photos: { status: "error", data: [] } }
 *
 * A template should not have to know about that envelope — `{{ split.km }}`
 * and `data-arcee-repeat="splits"` want the list itself, so `.data` is
 * unwrapped into the scope.
 *
 * WHY FAILURE AND EMPTINESS ARE KEPT APART. Both produce an empty list, so it
 * is tempting to treat them identically — but they mean opposite things to a
 * visitor. "No splits recorded" is a fact; showing it because the splits table
 * was unreachable is a falsehood the visitor cannot detect and the operator
 * never hears about. The failed aliases are therefore reported separately, so
 * the renderer can show an "unavailable" state instead of an "empty" one.
 *
 * Degrading quietly is right. Asserting something false while degrading is not.
 */

/** Unwraps each block's `.data` into a flat scope for the template engine. */
export function buildRelatedScope(related) {
  const scope = {};
  if (!related || typeof related !== 'object') return scope;

  for (const [alias, block] of Object.entries(related)) {
    if (block && typeof block === 'object' && 'data' in block) {
      scope[alias] = block.data;
    } else {
      // Tolerate a bare value, so a future endpoint shape change degrades
      // rather than blanking every related block at once.
      scope[alias] = block;
    }
  }
  return scope;
}

/**
 * Aliases whose block failed to load, as a Set.
 *
 * Empty for a healthy response, so callers can treat "no failures" as the
 * common case without a null check.
 */
export function failedAliases(related) {
  const failed = new Set();
  if (!related || typeof related !== 'object') return failed;

  for (const [alias, block] of Object.entries(related)) {
    if (block && typeof block === 'object' && block.status === 'error') {
      failed.add(alias);
    }
  }
  return failed;
}
